import { useState, useEffect } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import { notificationApi } from '@/lib/api';

export default function NotificationBanner() {
  const [show, setShow] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem('notif_dismissed');
    const isSubscribed = localStorage.getItem('push_subscribed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }
    if (isSubscribed) {
      setSubscribed(true);
      return;
    }
    // The browser already knows the answer: allowed (subscribed earlier, maybe on another visit) or blocked.
    // Asking again is only noise, so the banner stays away either way.
    if (typeof Notification !== 'undefined' && Notification.permission !== 'default') {
      if (Notification.permission === 'granted') setSubscribed(true);
      return;
    }
    // Show banner after 5 seconds
    const timer = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubscribe = async () => {
    if (!('Notification' in window)) {
      alert('Your browser does not support push notifications.');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      try {
        // Get VAPID key
        const keyRes = await notificationApi.getVapidKey();
        const vapidKey = keyRes.data.publicKey;

        if (vapidKey && 'serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.ready;
          const subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidKey,
          });
          await notificationApi.subscribe({
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
            },
            preferences: ['price_updates', 'new_coupons', 'promotions'],
          });
        } else {
          // No VAPID key or no service worker — store a simple subscription
          await notificationApi.subscribe({
            endpoint: `browser_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            keys: { browser: navigator.userAgent },
            preferences: ['price_updates', 'new_coupons', 'promotions'],
          });
        }

        localStorage.setItem('push_subscribed', 'true');
        setSubscribed(true);
        setShow(false);
      } catch (err) {
        console.error('Push subscription failed:', err);
        // Store as in-app notification preference
        await notificationApi.subscribe({
          endpoint: `inapp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          keys: { type: 'in-app' },
          preferences: ['price_updates', 'new_coupons', 'promotions'],
        });
        localStorage.setItem('push_subscribed', 'true');
        setSubscribed(true);
        setShow(false);
      }
    }
  };

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    localStorage.setItem('notif_dismissed', 'true');
  };

  if (!show || subscribed || dismissed) return null;

  return (
    <div
      className="fixed bottom-[8rem] right-6 z-[9990] w-[320px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 p-4 animate-slide-up"
      data-testid="notification-banner"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        data-testid="notif-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h4 className="font-semibold text-sm text-gray-900">Stay Updated</h4>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
            Get notified about price drops, new coupons, and exclusive deals!
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleSubscribe}
              className="text-xs font-medium bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              data-testid="notif-subscribe-btn"
            >
              Enable Notifications
            </button>
            <button
              onClick={handleDismiss}
              className="text-xs text-gray-400 hover:text-gray-600"
              data-testid="notif-later-btn"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
